import { useSessionStore } from "@auth/sessionStore";
import { CompanionPanel } from "@components/CompanionPanel";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RecurrenceCard } from "@recurrences/RecurrenceCard";
import { RecurrenceFormModal } from "@recurrences/RecurrenceFormModal";
import type { Recurrence } from "@recurrences/recurrencesClient";
import { useRecurrencesList } from "@recurrences/recurrencesQueries";
import {
  Button,
  EmptyNote,
  PanelError,
  SkeletonStack,
} from "@scribenote/brian-design-system";
import { useState } from "react";

export const RecurrencesPanel = () => {
  // `undefined` = closed, `null` = creating, a recurrence = editing.
  const [editing, setEditing] = useState<Recurrence | null | undefined>(
    undefined,
  );

  return (
    <div className="flex h-full flex-col gap-3 p-2 font-body">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-annotation-caps text-annotation">Recurrences</h2>
        <Button
          size="sm"
          onClick={() => setEditing(null)}
          data-testid="recurrence-new"
        >
          <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
          New recurrence
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <CompanionPanel
          skeleton={<RecurrencesSkeleton />}
          fallback={(retry) => (
            <PanelError label="recurrences" onRetry={retry} />
          )}
        >
          <RecurrencesList onEdit={setEditing} />
        </CompanionPanel>
      </div>

      {editing !== undefined && (
        <RecurrenceFormModal
          key={editing?.id ?? "new"}
          recurrence={editing ?? undefined}
          isOpen
          onClose={() => setEditing(undefined)}
        />
      )}
    </div>
  );
};

const RecurrencesList = ({
  onEdit,
}: {
  onEdit: (recurrence: Recurrence) => void;
}) => {
  const sessionKey = useSessionStore((s) => s.clinic?.clinicUuid ?? "");
  const { data: recurrences } = useRecurrencesList(sessionKey, null);

  if (recurrences.length === 0)
    return <EmptyNote>Nothing on a schedule yet.</EmptyNote>;

  return recurrences.map((r) => (
    <RecurrenceCard key={r.id} recurrence={r} onEdit={onEdit} />
  ));
};

const RecurrencesSkeleton = () => (
  <SkeletonStack
    count={4}
    className="gap-3"
    itemClassName="h-24 border border-rule"
  />
);
